import {EventEmitter, Injectable} from '@angular/core';
import {AgreementDestination} from '../../core/configuration/ui-config';
import {AgreementModel, AgreementValueModel} from '../../core/models/agreement.model';
import {ConfigurationService} from '../../core/services/impl/configuration/configuration.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import * as _ from 'lodash';
import {ApplicationContextService} from '../../core/services/impl/configuration/application-context.service';
import {isMPay, isSkycash} from '../../core/utils/utils';
import {CalculationService} from '../../core/services/impl/calculation.service';
import {CalculationStorageService} from './calculation-storage.service';
import {UrlParamsService} from '../../core/services/impl/url-params.service';
import {StepKey} from '../../core/enums/step-key.enum';
import {LanguageService} from './language.service';

@Injectable({
  providedIn: 'root'
})
export class AgreementsService {

  agreementsCollectible: boolean;
  calculationAgreementSelectionUpdate$: EventEmitter<CalculationAgreementSelectionUpdateEvent> = new EventEmitter<CalculationAgreementSelectionUpdateEvent>();
  readonly REQUIRED_SYMBOL = '*';
  private readonly mappingConfig = [
    {
      suffix: 'YES',
      value: true
    },
    {
      suffix: 'NO',
      value: false
    }
  ];

  get calculationAgreements(): AgreementModel[] {
    return this.calculationService.agreements ? this.calculationService.agreements : [];
  }

  set calculationAgreements(agreements: AgreementModel[]) {
    this.calculationService.agreements = agreements;
  }

  get language(): string {
    return this.languageService.getDefaultLanguage().toUpperCase();
  }

  get readonlyAgreementsAllowed(): boolean {
    return !this.contextService.isDiagnosticStationContext;
  }

  constructor(private configuration: ConfigurationService,
              private contextService: ApplicationContextService,
              private calculationService: CalculationService,
              private calculationStorageService: CalculationStorageService,
              private languageService: LanguageService,
              private urlParamsService: UrlParamsService) {
  }

  init(): void {
    this.initAgreementsCollectability();
  }

  changeAgreement(code: string, selected: boolean): void {
    this.getAgreement(code).selectedAnswer = this.getSelectedAgreementAnswer(code, selected);
    this.calculationStorageService.updateCalculation();
    this.calculationAgreementSelectionUpdate$.emit({
      code,
      selected
    });
  }

  getAgreementsForDestination(stepKey?: StepKey): AgreementModel[] {
    if (isNotNullOrUndefined(this.configuration.uiConfig.agreementsConfig)) {
      const agreementCodes = this.configuration.uiConfig.agreementsConfig
        .filter(config => config.stepsAllowed.includes(stepKey) && !this.contextService.isAgreementDisabled(config.agreementCode))
        .map(config => config.agreementCode);
      const agreements = _.cloneDeep(this.configuration.partnerAgreements);
      return agreements.filter((agreement: AgreementModel) => {
        let agreementAllowed = agreementCodes.includes(agreement.code);
        if (stepKey === StepKey.FIRST_STEP_3) {
          agreementAllowed = agreementAllowed && !agreement.details.readonly && !this.isCalculationAgreementSelected(agreement.code);
        }
        return agreementAllowed;
      });
    }
    return [];
  }

  getValuesMapping(agreement: AgreementModel): any[] {
    const valuesMapping = [];
    agreement.answers.forEach(answer => {
      const value = this.mappingConfig.find(config => answer.code.endsWith(config.suffix)).value; // find boolean value for given answer.code
      if (isNotNullOrUndefined(value)) {
        valuesMapping.push({
          propertyValue: answer.code,
          checkboxValue: value
        });
      }
    });
    return valuesMapping;
  }

  getAgreementValue(agreement: AgreementModel): AgreementValueModel {
    const agreementValues = agreement.details.values.filter(item => item.languageCode.toUpperCase() === this.language);
    if (agreementValues.length > 0) {
      return agreementValues[0];
    } else {
      throw new Error(`Agreement does not contain value for given language code: ${this.language}.`);
    }
  }

  private initAgreementsCollectability(): void {
    this.agreementsCollectible = !((isMPay() || isSkycash()) && this.urlParamsService.isMobileOrigin);
    if (this.contextService.isDiagnosticStationContext) {
      this.agreementsCollectible = false;
    }
  }

  private getAgreement(code: string): AgreementModel {
    return this.calculationService.agreements.filter(item => item.code === code)[0];
  }

  private getSelectedAgreementAnswer(code: string, selected: boolean): string {
    const answers = this.getAgreement(code).answers;
    const suffix = selected ? 'YES' : 'NO';
    return answers.find(answer => answer.code.endsWith(suffix)).code;
  }

  private isCalculationAgreementSelected(agreementCode: string): boolean {
    const agreement = this.calculationAgreements.find(item => item.code === agreementCode);
    return agreement ? agreement.selectedAnswer.endsWith('YES') : false;
  }
}

export interface CalculationAgreementSelectionUpdateEvent {
  code: string;
  selected: boolean;
}
